import React, { useEffect, useRef, useState } from 'react';
import { getApiBaseUrl } from '../utils/domain';

const API_BASE_URL = getApiBaseUrl();

interface Voice {
  voice_id: string;
  name: string;
  category?: string;
}

interface VoiceSelectProps {
  value: string;
  onChange: (value: string) => void;
  onSelect?: (voice: { id: string; name: string | null }) => void;
  label?: string;
  helpText?: string;
}

const VoiceSelect: React.FC<VoiceSelectProps> = ({
  value,
  onChange,
  onSelect,
  label = 'Voice',
  helpText,
}) => {
  const [voices, setVoices] = useState<Voice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchVoices = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/elevenlabs/voices`);

        if (!response.ok) {
          throw new Error(`Failed to load voices: ${response.statusText}`);
        }

        const data = await response.json();
        if (isMounted) {
          setVoices(data.voices || []);
        }
      } catch (err) {
        console.error('Error fetching voices:', err);
        if (isMounted) {
          setError('Unable to load voices. Paste a voice ID instead.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchVoices();

    return () => {
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
      }
      isMounted = false;
    };
  }, []);

  const normalizedValue = value || '';
  const selectedVoice = voices.find((voice) => voice.voice_id === normalizedValue);
  const legacyLabel = normalizedValue === 'vocal'
    ? 'Legacy: Instant Voice Copy'
    : normalizedValue === 'professional'
      ? 'Legacy: Professional Voice Copy'
      : normalizedValue === 'custom'
        ? 'Legacy: Custom Voice'
        : null;

  const handleChange = (nextValue: string) => {
    onChange(nextValue);
    if (onSelect) {
      const matchedVoice = voices.find((voice) => voice.voice_id === nextValue);
      onSelect({ id: nextValue, name: matchedVoice?.name || null });
    }
  };

  const handlePreview = async () => {
    if (!normalizedValue) {
      setPreviewError('Select a voice to preview.');
      return;
    }
    if (legacyLabel) {
      setPreviewError('Legacy voice detected. Choose a new voice to preview.');
      return;
    }

    try {
      setPreviewError(null);
      setIsPreviewing(true);

      const response = await fetch(`${API_BASE_URL}/api/elevenlabs/voices/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          voice_id: normalizedValue,
          text: 'Hi, I am your double. How can I help today?',
        }),
      });

      if (!response.ok) {
        throw new Error(`Preview failed: ${response.statusText}`);
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);

      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
      }

      audioUrlRef.current = audioUrl;
      audioRef.current = new Audio(audioUrl);
      await audioRef.current.play();
    } catch (err) {
      console.error('Voice preview error:', err);
      setPreviewError('Preview failed. Try again or select another voice.');
    } finally {
      setIsPreviewing(false);
    }
  };

  return (
    <div>
      <label htmlFor="voiceSelect" className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      {helpText && (
        <p className="text-xs text-gray-500 mb-2">{helpText}</p>
      )}
      {isLoading ? (
        <div className="text-sm text-gray-500">Loading voices...</div>
      ) : voices.length > 0 ? (
        <select
          id="voiceSelect"
          value={normalizedValue}
          onChange={(e) => handleChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        >
          <option value="" disabled>
            Select a voice
          </option>
          {voices.map((voice) => (
            <option key={voice.voice_id} value={voice.voice_id}>
              {voice.name}{voice.category ? ` • ${voice.category}` : ''}
            </option>
          ))}
          {legacyLabel && (
            <option value={normalizedValue}>{legacyLabel}</option>
          )}
          {normalizedValue && !selectedVoice && !legacyLabel && (
            <option value={normalizedValue}>
              Custom voice ({normalizedValue})
            </option>
          )}
        </select>
      ) : (
        <input
          type="text"
          value={normalizedValue}
          onChange={(e) => handleChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          placeholder="Paste ElevenLabs voice ID"
        />
      )}
      <div className="mt-3 flex items-center gap-3">
        <button
          type="button"
          onClick={handlePreview}
          disabled={!normalizedValue || isPreviewing}
          className="px-3 py-2 text-sm font-medium text-primary border border-primary/30 rounded-lg hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isPreviewing ? 'Previewing...' : 'Preview voice'}
        </button>
        {selectedVoice && (
          <span className="text-xs text-gray-500">
            {selectedVoice.name}
          </span>
        )}
      </div>
      {error && (
        <p className="text-xs text-amber-600 mt-2">{error}</p>
      )}
      {previewError && (
        <p className="text-xs text-red-500 mt-2">{previewError}</p>
      )}
    </div>
  );
};

export default VoiceSelect;
